import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../../shared/helpers/icons';
import { EopAssistantComponent } from '../../components/eop-assistant/eop-assistant.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { CardsListSkeletonComponent } from '../../components/cards-list-skeleton/cards-list-skeleton.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IContact, IUserInfo } from '../../../shared/models/contact';
import { ContactsService } from '../../../shared/services/contact.service';
import { UserDataService } from '../../../shared/services/userData.service';
import { IPosition } from '../../../shared/models/position';
import { PositionsService } from '../../../shared/services/position.service';
import { IPositionContact } from '../../../shared/models/position';
import { ContactInfoPopupComponent } from '../../components/contact-info-popup/contact-info-popup.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { SpinnerComponent } from "../../components/spinner/spinner.component";
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { DeleteConfirmationComponent } from "../../components/delete-confirmation/delete-confirmation.component";
import { EopNavigationService } from '../../../shared/services/eopNavigation.service';
import { EEopTemplates, ESectionTypes } from '../../../shared/models/eop_types.enum';
import { NoEmojiDirective } from '../../../shared/helpers/noEmoji.directive';

@Component({
  selector: 'app-nims-ics',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    EopAssistantComponent,
    CardsListSkeletonComponent,
    PrvNextButtonsComponent,
    ContactInfoPopupComponent,
    PopupMessageComponent,
    SpinnerComponent,
    IntroductionComponent,
    DeleteConfirmationComponent,
    NoEmojiDirective
],
  templateUrl: './nims-ics.component.html',
  styleUrl: './nims-ics.component.css'
})
export class NimsIcsComponent implements OnInit {

  currentRoute: string = 'nims-ics';
  sectionType = ESectionTypes['ICS Roles'];

  icons = Icons;
  searchQuery: string = '';
  fullContactsList: IContact[] = [];
  contactsList: IContact[] = [];
  selectedContacts: IContact[] = [];
  positionsList: IPosition[] = [];
  contactInfo: IContact | null = null;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  selectedPositionId: string = '1';
  selectedPositions: IPositionContact[] = [];

  deleteConfirm: boolean = false;
  messageModal: string | null = null;
  popupMessage: string = '';
  showEOPAssistant: boolean = false;
  isPrimary: boolean = false;
  loading: boolean = false;
  primaryMatch: boolean = false;

  constructor(
    private userDataService: UserDataService,
    private contactsService: ContactsService,
    private positionsService: PositionsService,
    public eopNavigationService: EopNavigationService
  ) {}
  
  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadContacts();
    this.loadPositions();
    this.loadPositionContacts();
  }

  // load all Contacts and Contacts of a department
  loadContacts(): void {
    this.loading = true;
    this.contactsService.getContacts(this.currentBuildingId!).subscribe({
      next: (response) => {
        this.fullContactsList = response.data || [];

        // Check if user is an nside.io user
        if (this.currentUser?.email?.toLowerCase().endsWith('@nside.io')) {
          // Show full list
          this.contactsList = [...this.fullContactsList];
        } else {
          // Show only non-nside.io contacts
          this.contactsList = this.fullContactsList.filter(
            (c: any) => !c.email?.toLowerCase().endsWith('@nside.io')
          );
        }
        // Sort the list after retrieving contacts list.
        if (this.contactsList.length > 0) {
          this.contactsList.sort((a, b) => a.name.localeCompare(b.name));
        }
        this.loading = false;
        this.loadPositionContacts();
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error loading contacts:', error.message);
      }
    });
  }; 

  filterContacts(): void {
    const query = this.searchQuery.toLowerCase().trim();
  
    this.contactsList = this.fullContactsList.filter((item) => {
      return (
        item.name.toLowerCase().includes(query) ||
        item.Title?.toLowerCase().includes(query) ||
        item.workphone?.toLowerCase().includes(query) ||
        item.email?.toLowerCase().includes(query)
      );
    });
  }

  loadPositions(): void {
    this.loading = true;
    this.positionsService.getPositions(EEopTemplates['REMs EOP']).subscribe({
      next: (response) => {
        this.loading = false;
        if (!response.has_error) {
          this.positionsList = response.data ? response.data : [];
        }
      },
      error: (error) => {
        this.loading = false;
        console.error('Error fetching positions:', error.message);
      }
    });
  };

  loadPositionContacts(): void {
    if (this.currentBuildingId) {
      this.positionsService.getPositionContacts(this.currentBuildingId, EEopTemplates["REMs EOP"]).subscribe({
        next: (response) => {
          this.selectedPositions = response.data || [];
          // Map the response data to the expected format
          this.selectedPositions.map((item) => ({
            placement: item.placement,
            building_id: item.building_id,
            position_id: item.position_id,
            contact_id: item.contact_id
          }));
          console.log('Initital list of position contacts: ', response);
        },
        error: (error) => {
          console.error('Error fetching positions:', error.message);
        }
      });
    }
  };
  
  setSelectedPositionId(positionId: string) {
    this.selectedPositionId = positionId + '';
    console.log('Selected Position ID:', this.selectedPositionId);
  }

  // open contact info pop up
  openInfo(contact: IContact): void {
    this.contactInfo = contact;
  };

  assignContact(contactId: string, positionId: string, placement: number) {
    if (this.currentBuildingId) {
      
    // Filter the existing assignments for the specified position
    const existingAssignments = this.selectedPositions.filter(
      entry => entry.position_id.toString() === positionId
    );
  
    // Check if the contact is already assigned to this position
    const isContactAlreadyAssigned = existingAssignments.some(
      entry => entry.contact_id.toString() === contactId
    );
    const assignedContact = this.contactsList.find(c => c.id+'' === contactId);

    if (isContactAlreadyAssigned) {
      alert(`Contact ${assignedContact!.name} is already assigned to this position.`);
      return;
    }
  
    // Define a new PositionContact assignment
    const newAssignment: IPositionContact = {
      placement,
      building_id: Number(this.currentBuildingId),
      position_id: Number(positionId),
      contact_id: Number(contactId)
    };

    const existingPrimary = existingAssignments.find(entry => entry.placement === 1);
    if (placement === 1) {
      // primary existed => alert
      if (existingPrimary) {
        alert("You've already assigned a primary contact for this position. Please remove to add a new one.");
        return
      } else {
        // no primary => add primary
        this.selectedPositions = [
          ...this.selectedPositions.filter(
            entry => !(entry.position_id.toString() === positionId && entry.placement === 1)
          ),
          newAssignment
        ];
      }
    } else {
      if (!existingPrimary) {
        alert(`You need to assigned Primary contact for this position first.`);
        return
      } else {
        // Check if alternate slots (2 and 3) are available
        const existingAlternate1 = existingAssignments.find(entry => entry.placement === 2);
        const existingAlternate2 = existingAssignments.find(entry => entry.placement === 3);
    
        if (!existingAlternate1) {
          newAssignment.placement = 2;
        } else if (!existingAlternate2) {
          newAssignment.placement = 3;
        } else {
          alert('Both alternate slots are occupied. Please remove one to add a new contact.');
          return;
        }
    
        // Update selectedPositions array with the new alternate assignment
        this.selectedPositions = [
          ...this.selectedPositions.filter(
            entry => !(entry.position_id.toString() === positionId && entry.placement === newAssignment.placement)
          ),
          newAssignment
        ];
      }
    }
    }
  };

  removeContact(positionId: number, placement: number): void {
    // Find the index of the contact with the given positionId and placement
    const index = this.selectedPositions.findIndex(
      contact => contact.position_id === positionId && contact.placement === placement
    );
    // Remove the contact if found
    if (index !== -1) {
      this.selectedPositions.splice(index, 1);
    }
  }

  getPrimaryContact(positionId: any): IContact | null {
    // Find the primary position contact with placement 1 in selectedPositions
    const primaryAssignment = this.selectedPositions.find(
      a => a.position_id === positionId && a.placement === 1
    );
    // Find the matching contact in contactsList based on contact_id
    return primaryAssignment
      ? this.contactsList.find(contact => contact.id === primaryAssignment.contact_id) || null
      : null;
  }
  
  getAlternateContact(positionId: number, alternateNumber: number): IContact | null {
    // Find the alternate position contact with the given placement in selectedPositions
    const alternateAssignment = this.selectedPositions.find(
      a => a.position_id === positionId && a.placement === alternateNumber
    );
  
    // Find the matching contact in contactsList based on contact_id
    return alternateAssignment
      ? this.contactsList.find(contact => contact.id === alternateAssignment.contact_id) || null
      : null;
  }

  // Check if the position has primary contact
  hasPrimary(positionId: string): boolean {
    return this.selectedPositions.some(
      el => el.position_id === parseInt(positionId) && el.placement === 1
    );
  }
  // Check if the position has alternate 1
  hasAlternate1(positionId: string): boolean {
    return this.selectedPositions.some(
      el => el.position_id === parseInt(positionId) && el.placement === 2
    );
  }
  // Check if the position has alternate 2
  hasAlternate2(positionId: string): boolean {
    return this.selectedPositions.some(
      el => el.position_id === parseInt(positionId) && el.placement === 3
    );
  }
  // Check if the position has 2 alternates
  hasTwoAlternates(positionId: string): boolean {
    const alternates = this.selectedPositions.filter(
      el => (el.position_id === parseInt(positionId) && el.placement === 2) || (el.position_id === parseInt(positionId) && el.placement === 3)
    );
    return alternates.length >= 2;
  }

  isContactPrimary(contactId: string, positionId: string): boolean {
    return this.selectedPositions.some(
      p => p.contact_id === parseInt(contactId) && p.position_id === parseInt(positionId) && p.placement === 1
    );
  }  

  isContactAlternate(contactId: string, positionId: string): boolean {
    return this.selectedPositions.some(
      p =>
        p.contact_id === parseInt(contactId) &&
        p.position_id === parseInt(positionId) &&
        (p.placement === 2 || p.placement === 3)
    );
  } 
  
  saveSelectedPosition(): void {
    console.log('New position contacts list to save: ',this.selectedPositions);
    // get a list of all position Id in the selected Position.
    const uniquePositionIds = [...new Set(this.selectedPositions.map(contact => contact.position_id))];
    // check if the position has alternate(s) but no primary => return error
    for (const index in uniquePositionIds) {
      let positionId = uniquePositionIds[index].toString();
      // case: no primary but alternate1 or alternate2 or 2 alternates
      if ((this.hasAlternate1(positionId.toString()) || 
          this.hasAlternate2(positionId.toString()) || 
          this.hasTwoAlternates(positionId.toString())) && (!this.hasPrimary(positionId.toString())))
      {
        let positionName = this.positionsList.find(entry => entry.id === parseInt(positionId))!.position_name;
        this.loading = false;
        alert(`Position ${positionName} does not have a primary contact but alternate(s).`);
        console.log(positionId, positionName);
        return
      // case: primary and alternate2 but no alternate1
      } else if (this.hasPrimary(positionId.toString()) && this.hasAlternate2(positionId.toString()) && !this.hasAlternate1(positionId.toString()))
      {
        let positionName = this.positionsList.find(entry => entry.id === parseInt(positionId))!.position_name;
        this.loading = false;
        alert(`Position ${positionName} does not have an alternate 1 contact but alternate 2.`);
        console.log(positionId, positionName);
        return
      }
    };
  
    if (this.selectedPositions.length > 0 && this.currentBuildingId) {
      this.loading = true;
      // Save selected positions
      this.positionsService.savePositionContacts(this.currentBuildingId, this.selectedPositions).subscribe({
        next: (response) => {
          this.loading = false;
          this.messageModal = 'success';
          this.popupMessage = 'This occurred after saving ICS roles.';
          console.log('Successfully saving contacts to department:', response);
          this.loadPositionContacts();
        },
        error: (error) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
          console.error('Error saving contacts to department:', error.message);
          this.loadPositionContacts();
        }
      });
    }
  }

  deleteAllRecords(buildingId?: string): void {
    if (this.currentBuildingId) {
      this.deleteConfirm = false;
      this.loading = true;
      // Perform deletion
      this.positionsService.deleteAllPositionContacts(this.currentBuildingId).subscribe({
        next: (response) => {
          this.loading = false;
          this.messageModal = 'success';
          this.popupMessage = 'This occurred after deleting ICS roles.';
          console.log('All records deleted successfully:', response);
          this.loadPositionContacts();
        },
        error: (error) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
          console.error('Error deleting all records:', error.message);
          this.loadPositionContacts();
        }
      });
    }
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }

  cancelDelete(): void {
    this.deleteConfirm = false;
  }
}
